import { useState, useEffect, useRef, useCallback } from 'react';
import type { StepContentProps } from '~/ui-rocco/multi-step-form';
import { Input } from '@rocco/components/input';
import { ArrowDown } from 'lucide-react';
import { StepContent } from '../StepContent';
import { HdeOptionList } from '../HDEOptionList';
import {
    getHdeFieldLabelClassName,
    getHdeInputClassName,
    getHdeStepSectionClassName,
    getHdeStepTitleClassName,
} from '../hde-option-styles';
import { usePlaceFinderDirect } from '~/ui-rocco/hooks/location/use-place-finder-direct';
import { useRootData } from '~/modules/root';
import type { SupportedCountry } from 'generated/graphql';
import { FindPlaceTypeEnum, FindPlaceApiProviderEnum } from 'generated/graphql';
import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';
import { formatLocation } from '@rocco/utils/format-location';
import type { LocationPlace } from '@rocco/ui/location/types/location-place';

const HDE_LOCATION_OPTIONS = [
    { value: 'Auckland', label: 'Auckland' },
    { value: 'Wellington', label: 'Wellington' },
    { value: 'Christchurch', label: 'Christchurch' },
    { value: 'Other NZ', label: 'Other NZ' },
    { value: 'Australia', label: 'Australia' },
];

/** Returns true if keyword looks like a postcode (pure digits, 2–5 chars). */
function isPostcodeLike(keyword: string): boolean {
    return /^\d{2,5}$/.test(keyword.trim());
}

interface ProjectLocationData {
    projectLocation?: string;
    selectedLocation?: DetailedPlace | null;
    showOtherInput?: boolean;
    /** Which option triggered the freeform input ('Other NZ' or 'Australia'). */
    customLocationSource?: CustomLocationSource;
    /** When 'company', uses company field names and "Where's your Company located?" title. */
    companyLocation?: string;
    companySelectedLocation?: DetailedPlace | null;
    companyShowOtherInput?: boolean;
    companyCustomLocationSource?: CustomLocationSource;
    /** Pass 'company' for company location step; uses company field names and title. */
    variant?: LocationStepVariant;
}

type LocationStepVariant = 'project' | 'company';
type CustomLocationSource = 'Other NZ' | 'Australia';

function inferCustomLocationSource(
    location: string,
    selectedLocation: DetailedPlace | null,
): CustomLocationSource {
    const normalizedCountry = selectedLocation?.country?.trim().toUpperCase();
    const normalizedLocation = location.trim().toUpperCase();

    if (
        normalizedCountry === 'AU' ||
        normalizedCountry === 'AUSTRALIA' ||
        normalizedLocation === 'AUSTRALIA' ||
        normalizedLocation.endsWith(', AUSTRALIA')
    ) {
        return 'Australia';
    }

    return 'Other NZ';
}

/** Format place for display in the dropdown and input. */
function formatPlaceForDisplay(place: DetailedPlace): string {
    if (place.fullAddress) return place.fullAddress;
    const locationPlace: LocationPlace = {
        suburb: place.suburb,
        district: place.district,
        city: place.city,
        region: place.region,
        state: place.state,
        fullAddress: place.fullAddress,
        latitude: place.latitude,
        longitude: place.longitude,
        country: place.country,
        address1: place.address1,
        address2: place.address2,
        addressID: place.addressID,
        depth: place.depth,
        parentID: place.parentID,
        placeName: place.placeName,
        postCode: place.postCode,
        serviceableAreaID: place.serviceableAreaID,
    };
    return formatLocation(locationPlace);
}

export function ProjectLocationStep({
    setIsValid,
    onDataChange,
    onNext,
    initialData,
}: StepContentProps) {
    const data = initialData as ProjectLocationData | undefined;
    const isCompany = data?.variant === 'company';
    const location = isCompany
        ? (data?.companyLocation ?? '')
        : (data?.projectLocation ?? '');
    const selectedLoc = isCompany
        ? (data?.companySelectedLocation ?? null)
        : (data?.selectedLocation ?? null);
    const showOther = isCompany
        ? (data?.companyShowOtherInput ?? false)
        : (data?.showOtherInput ?? false);

    const [projectLocation, setProjectLocation] = useState(location || '');
    const [selectedLocation, setSelectedLocation] =
        useState<DetailedPlace | null>(selectedLoc);
    const [keyword, setKeyword] = useState(
        selectedLoc ? formatPlaceForDisplay(selectedLoc) : location || '',
    );
    const persistedSource = isCompany
        ? data?.companyCustomLocationSource
        : data?.customLocationSource;
    const [showOtherInput, setShowOtherInput] = useState(showOther);
    const [customLocationSource, setCustomLocationSource] =
        useState<CustomLocationSource>(
            persistedSource ?? inferCustomLocationSource(location, selectedLoc),
        );
    const [showSuggestions, setShowSuggestions] = useState(false);
    const otherInputRef = useRef<HTMLInputElement>(null);
    const justSelectedOtherRef = useRef(false);

    const { localisation } = useRootData();
    const searchCountry =
        customLocationSource === 'Australia'
            ? ('AU' as SupportedCountry)
            : ('NZ' as SupportedCountry);
    const { places, handlePlaceFind, resetPlaces } = usePlaceFinderDirect({
        country: showOtherInput
            ? searchCountry
            : (localisation.country as SupportedCountry),
        type: FindPlaceTypeEnum.All,
        apiProvider: FindPlaceApiProviderEnum.Mapbox,
        apiProviderSelector: (kw) =>
            isPostcodeLike(kw)
                ? FindPlaceApiProviderEnum.ServiceableArea
                : FindPlaceApiProviderEnum.Mapbox,
    });

    useEffect(() => {
        if (showOtherInput) {
            otherInputRef.current?.focus();
        }
    }, [showOtherInput]);

    /** Sync local state when parent resets (e.g. back button from Other NZ view). */
    useEffect(() => {
        const parentShowOther = isCompany
            ? data?.companyShowOtherInput
            : data?.showOtherInput;
        const parentLocation = isCompany
            ? data?.companyLocation
            : data?.projectLocation;
        const parentSelected = isCompany
            ? data?.companySelectedLocation
            : data?.selectedLocation;

        if (parentShowOther === false && showOtherInput) {
            if (justSelectedOtherRef.current) {
                justSelectedOtherRef.current = false;
                return;
            }
            setShowOtherInput(false);
            setProjectLocation(parentLocation ?? '');
            setSelectedLocation(parentSelected ?? null);
            setKeyword(
                parentSelected
                    ? formatPlaceForDisplay(parentSelected)
                    : (parentLocation ?? ''),
            );
        }
    }, [
        isCompany,
        data?.showOtherInput,
        data?.projectLocation,
        data?.selectedLocation,
        data?.companyShowOtherInput,
        data?.companyLocation,
        data?.companySelectedLocation,
        showOtherInput,
    ]);

    useEffect(() => {
        if (showOtherInput) {
            setIsValid(selectedLocation !== null);
        } else {
            // Require user to select a preset (Auckland, Wellington, etc.) before advancing
            setIsValid(projectLocation !== '');
        }
        if (isCompany) {
            onDataChange?.({
                companyLocation: projectLocation,
                companySelectedLocation: selectedLocation,
                companyShowOtherInput: showOtherInput,
                companyCustomLocationSource: customLocationSource,
            });
        } else {
            onDataChange?.({
                projectLocation,
                selectedLocation,
                showOtherInput,
                customLocationSource,
            });
        }
    }, [
        projectLocation,
        selectedLocation,
        showOtherInput,
        customLocationSource,
        isCompany,
        setIsValid,
        onDataChange,
    ]);

    const handleKeywordChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setKeyword(value);

            if (value.trim().length > 2) {
                const limit = isPostcodeLike(value) ? 50 : 10;
                handlePlaceFind(value, limit);
                setShowSuggestions(true);
            } else {
                resetPlaces();
                setShowSuggestions(false);
            }

            if (value.trim().length === 0) {
                setSelectedLocation(null);
                setProjectLocation('');
                setShowSuggestions(false);
            }
        },
        [handlePlaceFind, resetPlaces],
    );

    const handleSuggestionSelect = useCallback(
        (place: DetailedPlace) => {
            const formatted = formatPlaceForDisplay(place);
            setSelectedLocation(place);
            setProjectLocation(formatted);
            setKeyword(formatted);
            setShowSuggestions(false);
            resetPlaces();
        },
        [resetPlaces],
    );

    const handleSelectAndAdvance = (value: string) => {
        if (value === 'Other NZ' || value === 'Australia') {
            justSelectedOtherRef.current = true;
            setCustomLocationSource(value as CustomLocationSource);
            setShowOtherInput(true);
            setProjectLocation('');
            setSelectedLocation(null);
            setKeyword('');
            return;
        }
        setSelectedLocation(null);
        if (isCompany) {
            onDataChange?.({
                companyLocation: value,
                companySelectedLocation: null,
            });
        } else {
            onDataChange?.({ projectLocation: value, selectedLocation: null });
        }
        onNext();
    };

    const locationTitle = isCompany
        ? "Where's your Company located?"
        : "Where's your project located?";

    if (showOtherInput) {
        return (
            <div className="flex min-h-0 flex-1 flex-col gap-[38px]">
                <StepContent variant="guest">
                    <div className={getHdeStepSectionClassName()}>
                        <label className={getHdeStepTitleClassName()}>
                            {locationTitle}
                        </label>
                        <div className="flex w-full max-w-[465px] flex-col gap-7">
                            <div
                                className="flex min-h-[3rem] w-full items-center justify-between rounded-[0.7916rem] border-0 border-transparent bg-[#FDFDFD] px-[0.7916rem] py-[0.9676rem]"
                                aria-hidden
                            >
                                <span className="text-[0.9185rem] leading-[1.1] tracking-[-0.0184rem] text-[#2A2A2A]">
                                    {customLocationSource}
                                </span>
                                <ArrowDown className="size-3.5 shrink-0 text-[#38383B]" />
                            </div>

                            <div className="relative flex flex-col gap-2">
                                <label
                                    htmlFor={
                                        isCompany
                                            ? 'company-location-other'
                                            : 'project-location-other'
                                    }
                                    className={getHdeFieldLabelClassName()}
                                >
                                    Enter location
                                </label>
                                <div className="relative">
                                    <Input
                                        id={
                                            isCompany
                                                ? 'company-location-other'
                                                : 'project-location-other'
                                        }
                                        ref={otherInputRef}
                                        type="text"
                                        value={keyword}
                                        autoComplete="off"
                                        onChange={handleKeywordChange}
                                        placeholder={
                                            isCompany
                                                ? 'Enter your Company location'
                                                : 'Enter your Project location'
                                        }
                                        className={getHdeInputClassName()}
                                    />

                                    {showSuggestions && places.length > 0 && (
                                        <ul
                                            role="listbox"
                                            aria-label="Location suggestions"
                                            className="scrollbar-elegant absolute left-0 right-0 top-full z-10 mt-1 max-h-32 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg md:max-h-36"
                                        >
                                            {places.map((place, index) => (
                                                <li
                                                    key={index}
                                                    role="option"
                                                    aria-selected={false}
                                                >
                                                    <button
                                                        type="button"
                                                        className="w-full cursor-pointer border-b border-gray-100 px-4 py-3 text-left last:border-b-0 md:hover:bg-gray-50"
                                                        onClick={() =>
                                                            handleSuggestionSelect(
                                                                place,
                                                            )
                                                        }
                                                    >
                                                        <span className="block text-sm text-gray-900">
                                                            {formatPlaceForDisplay(
                                                                place,
                                                            )}
                                                        </span>
                                                        {place.postCode && (
                                                            <span className="mt-1 hidden text-xs text-gray-500 md:block">
                                                                {place.postCode}
                                                            </span>
                                                        )}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </StepContent>
            </div>
        );
    }

    const mainLabel = isCompany
        ? "Where's your Company located?"
        : 'What is the location of your Project?';

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <StepContent>
                <HdeOptionList
                    label={mainLabel}
                    options={HDE_LOCATION_OPTIONS}
                    mode="single"
                    value={projectLocation || null}
                    onChange={setProjectLocation}
                    appearance="arrow"
                    onSelectAndAdvance={handleSelectAndAdvance}
                />
            </StepContent>
        </div>
    );
}
